import { isNullOrUndefined } from '../../util/utils';
import { HitData, SearchColumn } from '../../store/common/search/types';
import { CommonContextState } from '../../store/context/types';

export interface AppSpecifiedSearchHitService {
    getFooterOption: (hit: HitData, dataType?: string) => any;
    getHitSelectionValue: (context: CommonContextState, dataType?: string,) => any;
    getHitSelectionKey: (dataType?: string) => string;
    getHitPrimaryIdValue: (hitData: HitData, dataType?: string) => any;
    getHitAssessmentRunId?: (hitData: HitData) => any;
    getHitUniqueIdValue?: (hitData: HitData, dataType?: string) => string;
    isActiveHit: (hitData: HitData, context: CommonContextState, dataType?: string,) => boolean;
    getHitPrimaryNameValue: (hitData: HitData, dataType?: string,) => any;
    hasAccessToActions?: (dataType: string) => boolean;
    getAlternativeUserIdIfExists?: () => number;
    getActiveHitTitle?: (hits: HitData[], context: CommonContextState) => string;
    getQuestionWeight?: (hitData: HitData) => number;
}

let appSpecifiedSearchHitService: AppSpecifiedSearchHitService;

const commonSearchHitService = {
    getCurrentHitIndex(dataType: string, context: CommonContextState, hits: HitData[]) {
        const footerSelectionValue = appSpecifiedSearchHitService.getHitSelectionValue(context, dataType);
        const selectionKey: string = appSpecifiedSearchHitService.getHitSelectionKey(dataType);

        return hits.findIndex((hit: HitData) => {
            return footerSelectionValue === hit[selectionKey];
        });
    },
    getHitIndexForValue(dataType: string, hits: HitData[], searchValue: any) {
        const selectionKey: string = appSpecifiedSearchHitService.getHitSelectionKey(dataType);
        return hits.findIndex((hit: HitData) => {
            return searchValue === hit[selectionKey];
        });
    },
    getNestedUserValueFromHit(hitData: HitData, searchColumn: SearchColumn, userId: number, collaborativeEvaluatorId?: number,
        contextEvaluationRound?: number) {
        const { nested } = searchColumn;
        if (nested) {
            const path: string = nested[0];
            const pathInHit: HitData[] = hitData[path];
            if (pathInHit && pathInHit.length) {
                if (contextEvaluationRound) {
                    const filteredPathInHit = pathInHit.filter((grade: any) => grade.evaluationRound === contextEvaluationRound);
                    if (filteredPathInHit.length) {
                        return filteredPathInHit.find(
                            (grade: any) => grade.evaluatorId === userId || grade.evaluatorId === collaborativeEvaluatorId
                        );
                    }
                } else {
                    return pathInHit.find((grade: any) => grade.evaluatorId === userId || grade.evaluatorId === collaborativeEvaluatorId);
                }
            }
        }
        return null;
    },
    getValuesFromHit(hitData: HitData, searchColumn: SearchColumn) {
        const { nested } = searchColumn;
        if (nested && nested.length === 2) {
            const path: string = nested[0];
            if (Array.isArray(hitData[path])) {
                return hitData[path];
            }
            if (!isNullOrUndefined(hitData[path])) {
                return [hitData[path]];
            }
            return [];
        }
        const path: string = searchColumn.name;
        if (!isNullOrUndefined(hitData[path])) {
            return [{ [path]: hitData[path] }];
        }
        return [];
    },
    getColumnValueFromHit(hitData: HitData, searchColumn: SearchColumn, userId?: number) {
        const { nested } = searchColumn;
        const alternativeUserId: number = typeof appSpecifiedSearchHitService.getAlternativeUserIdIfExists === 'function'
            && appSpecifiedSearchHitService.getAlternativeUserIdIfExists();
        const ids = [userId];
        if (alternativeUserId) {
            ids.push(alternativeUserId);
        }
        if (nested && nested.length === 2) {
            const path: string = nested[0];
            if (Array.isArray(hitData[path])) {
                if (searchColumn.valueBelongsToUser && ids.some((id: number) => !isNullOrUndefined(id))) {
                    const obj = hitData[path].find((hitValue: any) => ids.indexOf(hitValue.evaluatorId) > -1);
                    return obj ? obj[nested[1]] : null;
                }
                return hitData[path]
                    .reduce(
                        (acc: any, value: any) => {
                            if (value[nested[1]] !== undefined) {
                                acc.push(value[nested[1]]);
                            }
                            return acc;
                        },
                        []
                    );
            }
            return hitData[path][nested[1]];
        }
        const path: string = searchColumn.name;
        if (!isNullOrUndefined(hitData[path])) {
            return hitData[path];
        }
        return null;
    },
    isNestedColumn(searchColumn: SearchColumn, column: string) {
        const { nested } = searchColumn;
        if (nested && nested.length === 2) {
            const path: string = nested[0];
            if (path === column) {
                return true;
            }
        }

        return searchColumn.name === column;
    },
    getNestedValuesForHit(hitData: HitData, searchColumn: SearchColumn): any[] {
        const { nested } = searchColumn;
        if (nested && nested.length === 2) {
            const path: string = nested[0];
            const selector: string = nested[1];
            const pathInHit: HitData[] = hitData[path];
            if (pathInHit && pathInHit.length) {
                if (pathInHit.findIndex((hitData: HitData) => !isNullOrUndefined(hitData[selector])) > -1) {
                    return pathInHit.map((hitData: HitData) => hitData[selector]);
                }
            }
        }
        return [];
    },

    isHitInHitArray(dataType: string, hit: HitData, hits: HitData[]): boolean {
        if (!(hit) || !hits || !hits.length) {
            return false;
        }
        return commonSearchHitService.getHitIndexForValue(
            dataType,
            hits,
            appSpecifiedSearchHitService.getHitPrimaryIdValue(hit, dataType)
        ) !== -1;
    },
    areHitsEqual(dataType: string, firstHit: HitData, secondHit: HitData) {
        const selectionKey: string = appSpecifiedSearchHitService.getHitSelectionKey(dataType);
        return firstHit[selectionKey] === secondHit[selectionKey];
    },
};

export const initializeCommonSearchHitService = (appSearchHitService: AppSpecifiedSearchHitService) => {
    appSpecifiedSearchHitService = appSearchHitService;
};

export const getAppSpecifiedSearchHitService = () => {
    return appSpecifiedSearchHitService;
};
export default commonSearchHitService;
